import { DOCUMENT_KINDS, DEFAULT_ATTENDEES_LIMIT_PER_BOOKING } from "@/constants";
import { INPUT_TYPES } from "@/features/metafields/constants";

const isMultiValuesCustomField = (block, field) => {
  return [INPUT_TYPES.CHECKBOX, INPUT_TYPES.MULTISELECT].includes(
    window.SchoolSettings.metafields[block][field].input_type,
  );
};

const isTextAreaYesNo = (block, field) => {
  return (
    window.SchoolSettings.metafields[block][field].input_type ===
    INPUT_TYPES.TEXTAREA_YES_NO
  );
};

export const getCustomFieldInitialValue = (block, field) => {
  if (isMultiValuesCustomField(block, field)) {
    return [];
  } else if (isTextAreaYesNo(block, field)) {
    return { confirm_flag: null, text: "" };
  } else {
    return "";
  }
};

export default {
  getAddress() {
    return {
      apartment: "",
      street_address: "",
      suburb: "",
      state: "",
      postcode: "",
      country: null,
    };
  },

  getDoctor() {
    return {
      name: "",
      telephone: "",
      address: null,
    };
  },

  getDocumentGroup(kind) {
    return {
      documents: [],
      kind: kind,
      // Next fields are unavailable for parent via api, but they are required for staff.
      is_verified: null,
      note: "",
    };
  },

  getParent() {
    return {
      email: "",
      title: "",
      first_name: "",
      last_name: "",
      preferred_name: "",
      dob: null,
      relationship_to_student: null,
      residential_address: null,
      mailing_address: null,
      home_phone: "",
      business_phone: "",
      mobile_phone: "",
      religion: "",
      religion_other: "",
      church_membership: "",
      church_attended: "",
      nationalities: [],
      residency_details: null,
      born_country: null,
      year_of_arrival: null,
      is_speak_other_language: false,
      home_language: null,
      home_language_other: "",
      occupation: "",
      employer: "",
      industry: "",
      industry_other: "",
      occupational_group: null,
      highest_school_year: null,
      degree: null,
      alumnus: null,
      is_current_staff_member: null,
      is_health_care_card: null,
      health_care_card_number: "",
      health_care_card_expiry: "",
      health_care_card_documents: this.getDocumentGroup(
        DOCUMENT_KINDS.PARENT_HEALTH_CARE_CARD,
      ),
      email_preference: null,
      ethnic_origins: [],
      ethnic_origins_other: "",
      aboriginal_statuses: [],
      // `how_hear` field is provided only in documents step.
    };
  },

  getEvent() {
    return {
      name: "",
      kind: null,
      slug: "",
      is_unlisted: false,
      // Add default description for backward compatibility.
      // Some schools have extra css with selectors like `h5.h5.description`.
      // TODO: We can not support extra css classes in the HTML editor (HTMLSnippetEditor)
      description: `<div style="color: white;">
  <h5 style="max-width: 500px;" class="h5 description mx-auto">
  </h5>
</div>`,
      is_additional_attendees_only: false,
      attendees_limit_per_booking: DEFAULT_ATTENDEES_LIMIT_PER_BOOKING,
      is_public_kiosk_enabled: false,
      is_public_kiosk_self_check_in_enabled: false,
      is_public_kiosk_emails_enabled: false,
      online_meeting_link: "",
      is_parent_attendees_only: false,
      is_calendar_attached_to_mail: false,
      is_attendance_system_events_enabled: true,
      is_reminder_system_events_enabled: true,
      sessions: [this.getEventSession()],
      image: null,
      image_src: null,
    };
  },

  getEventSession() {
    return {
      name: "",
      is_session_enabled: true,
      date: null,
      start_time: null,
      end_time: null,
      capacity: null,
      registrations_limit: null,
      is_removable: true,
    };
  },

  // TODO: replace with api response
  getEventBooking() {
    return {
      booking_status: "",
      number_of_attendees: null,
      attendees: [],
      event: null, // Just for UI
      session: null,
      custom_field_1: getCustomFieldInitialValue("event_booking", "custom_field_1"),
      custom_field_2: getCustomFieldInitialValue("event_booking", "custom_field_2"),
      custom_field_3: getCustomFieldInitialValue("event_booking", "custom_field_3"),
      custom_field_1_other: "",
      custom_field_2_other: "",
      custom_field_3_other: "",
      event_booking_questionnaire: {},
      send_parent_email: false,
    };
  },

  getCurrentSchool() {
    return {
      current_school: null,
      current_school_other: "",
      current_school_grade: null,
      current_school_commence_date: null,
      current_school_history: "",
      nesa_number: "",
      vsn: "",
    };
  },

  // Required for staff, can't be removed.
  // TODO: fetch initial enquiry from backend
  getEnquiry() {
    return {
      questions: [],
      questions_other: "",
      comment: "",
      want_prospectus: false,
      preferred_contact_method: "",
      preferred_contact_method_other: "",
      custom_field_1: getCustomFieldInitialValue("enquiry", "custom_field_1"),
      custom_field_2: getCustomFieldInitialValue("enquiry", "custom_field_2"),
      custom_field_3: getCustomFieldInitialValue("enquiry", "custom_field_3"),
      custom_field_1_other: "",
      custom_field_2_other: "",
      custom_field_3_other: "",
    };
  },

  getAlumnus() {
    return {
      connection_type: "",
      connection_type_other: "",
      full_name: "",
      dob: null,
      house: "",
      year_attended_from: null,
      year_attended_to: null,
      what_school: "",
    };
  },

  getAttendee() {
    return {
      kind: "",
      title: "",
      first_name: "",
      last_name: "",
    };
  },

  getProfileResidencyDetails() {
    return {
      residency_status: null,
      visa_subclass: "",
      visa_arrival_date: null,
      visa_expiry_date: null,
      visa_expiry_unrestricted: null,
      is_vevo_access_permission_given: false,
      visa_documents: this.getDocumentGroup(DOCUMENT_KINDS.VISA),
      passport_documents: this.getDocumentGroup(DOCUMENT_KINDS.PASSPORT),
      australian_passport_documents: this.getDocumentGroup(
        DOCUMENT_KINDS.AUSTRALIAN_PASSPORT,
      ),
    };
  },

  getParentResidencyDetails() {
    return {
      residency_status: null,
      visa_subclass: "",
      visa_arrival_date: null,
      visa_expiry_date: null,
      visa_expiry_unrestricted: null,
      is_vevo_access_permission_given: false,
      australian_passport_documents: this.getDocumentGroup(
        DOCUMENT_KINDS.PARENT_AUSTRALIAN_PASSPORT,
      ),
    };
  },

  getEmergencyContact() {
    return {
      title: "",
      last_name: "",
      first_name: "",
      relationship_to_student: "",
      relationship_to_student_other: "",
      home_phone: "",
      mobile_phone: "",
      business_phone: "",
      email: "",
      is_authorized_to_collect_child: null,
      is_authorized_to_medication_consent: null,
      is_authorized_to_transport_consent: null,
      is_authorized_to_sign_permissions: null,
      is_emergency_event_notification: null,
      residential_address: null,
      mailing_address: null,
      custom_question_1: null,
      custom_question_2: null,
    };
  },

  getGuardian() {
    return {
      first_name: "",
      last_name: "",
      relationship_to_student: "",
      age: null,
      working_with_children_number: "",
      residential_address: null,
      home_phone: "",
      business_phone: "",
      mobile_phone: "",
      email: "",
      postal_address: "",
      profession: "",
    };
  },

  getSibling() {
    return {
      first_name: "",
      last_name: "",
      gender: null,
      dob: null,
      current_occupation: "",
      status: null,
      is_student: false,
      entry_year: null,
      house: "",
    };
  },

  getAgent() {
    return {
      title: "",
      first_name: "",
      last_name: "",
      company: "",
      email: "",
      contact_phone: "",
    };
  },
};
